/**
 * Decodes the `payload` of an ActionCable broadcast.
 *
 * Producers disagree on the encoding. `BroadcastingConcern#payload_for_broadcasting`
 * returns a rendered jbuilder response, which is a *string*, while the bulk
 * action jobs pass a Hash that reaches the wire as a nested object. Both land
 * on the same field of the same channel, so a consumer cannot assume either.
 *
 * The backend is moving to objects everywhere; until then this accepts both, so
 * a broadcast site can migrate on its own without breaking its consumers.
 *
 * **Route a consumer through this before the producer it reads flips.** A
 * producer change reaches every connected client the moment the server
 * restarts, including tabs on a bundle from days ago; this helper only reaches
 * a client that has reloaded. Shipping both halves in one commit is what broke
 * the classroom screen and pinboard boards on 2026-08-27 — see
 * `docs/design-docs/backend/action_cable.md`.
 *
 * **Delete this once every broadcast site sends an object** and no client still
 * reads one that does not. At that point the contract names the payload's
 * component and the generated client types it, so a consumer reads
 * `data.payload` directly and this helper only stands between it and the value.
 */
export function decodeCablePayload<T = Record<string, unknown>>(
  payload: unknown
): T {
  if (payload === undefined || payload === null || payload === "") {
    return {} as T;
  }

  return (typeof payload === "string" ? JSON.parse(payload) : payload) as T;
}
